export type MonthYear = {
  /** Calendar year (e.g. 2026) */
  year: number;
  /** 1-12 */
  month: number;
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
] as const;

function toSerialMonth({ year, month }: MonthYear): number {
  return year * 12 + (month - 1);
}

function fromSerialMonth(serial: number): MonthYear {
  const year = Math.floor(serial / 12);
  const month = (serial % 12) + 1;
  return { year, month };
}

export function addMonths(monthYear: MonthYear, deltaMonths: number): MonthYear {
  return fromSerialMonth(toSerialMonth(monthYear) + deltaMonths);
}

export function formatMonthYearShort({ year, month }: MonthYear): string {
  return `${MONTHS_SHORT[month - 1]} ${year}`;
}

export function formatMonthYearLong({ year, month }: MonthYear): string {
  return `${MONTHS_LONG[month - 1]} ${year}`;
}

export function formatMonthYearSlash({ year, month }: MonthYear): string {
  return `${year}/${String(month).padStart(2, '0')}`;
}

export function formatMonthYearDash({ year, month }: MonthYear): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Cutoff date logic (mirrors the previous Guideline.tsx implementation):
 * - If current date is on or before the 20th, use 2 months back
 * - If current date is >= 21st, use previous month
 *
 * Returned value is a month/year (no day component).
 */
export function getCutoffMonthYear(now: Date = new Date()): MonthYear {
  const currentDay = now.getDate();

  let cutoffMonth0 = now.getMonth(); // 0-11
  let cutoffYear = now.getFullYear();

  if (currentDay <= 20) {
    cutoffMonth0 -= 2;
    if (cutoffMonth0 < 0) {
      cutoffMonth0 += 12;
      cutoffYear -= 1;
    }
  } else {
    cutoffMonth0 -= 1;
    if (cutoffMonth0 < 0) {
      cutoffMonth0 = 11;
      cutoffYear -= 1;
    }
  }

  return { year: cutoffYear, month: cutoffMonth0 + 1 };
}

/**
 * "Latest month in pillar" as requested:
 * latestMonth = cutoffMonth - 1 month
 */
export function getLatestPillarMonthYear(now: Date = new Date()): MonthYear {
  const cutoff = getCutoffMonthYear(now);
  return addMonths(cutoff, -1);
}

export function diffMonths(from: MonthYear, to: MonthYear): number {
  return toSerialMonth(to) - toSerialMonth(from);
}

function quarterOf(monthYear: MonthYear): number {
  return Math.floor((monthYear.month - 1) / 3) + 1;
}

function halfOf(monthYear: MonthYear): number {
  return monthYear.month <= 6 ? 1 : 2;
}

function toQuarterIndex(year: number, quarter: number): number {
  return year * 4 + (quarter - 1);
}

function fromQuarterIndex(idx: number): { year: number; quarter: number } {
  const year = Math.floor(idx / 4);
  const quarter = (idx % 4) + 1;
  return { year, quarter };
}

const MONTH_NAME_TO_NUM: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12
};

function toMonthNum(name: string): number | undefined {
  return MONTH_NAME_TO_NUM[name.toLowerCase()];
}

function formatMonthName(month: number, style: 'short' | 'long'): string {
  return style === 'short' ? MONTHS_SHORT[month - 1] : MONTHS_LONG[month - 1];
}

/**
 * Shifts common reporting period tokens inside a question string by `deltaMonths`.
 *
 * Supported patterns (examples):
 * - "Oct 2025", "October 2025"
 * - "2025/10", "2025-10"
 * - "Aug to Oct 2025"
 * - "Jan 2025 to July 2025"
 * - "Q2 2025", "Q1, Q2 and Q3 2025"
 * - "H1 2025"
 * - "full year 2024"
 * - "2025 year-end"
 */
export function shiftReportingPeriodsInText(text: string, deltaMonths: number): string {
  let out = text;

  // 1) Month name + year (short/long): "Oct 2025", "October 2025"
  out = out.replace(/\b([A-Za-z]{3,9})\s+(\d{4})\b/g, (match, monRaw: string, yearRaw: string) => {
    const monthNum = toMonthNum(monRaw);
    if (!monthNum) return match;
    const shifted = addMonths({ year: Number(yearRaw), month: monthNum }, deltaMonths);
    const style: 'short' | 'long' = monRaw.length <= 3 ? 'short' : 'long';
    return `${formatMonthName(shifted.month, style)} ${shifted.year}`;
  });

  // 2) YYYY/MM
  out = out.replace(/\b(\d{4})\/(0[1-9]|1[0-2])\b/g, (_m, y: string, mm: string) => {
    const shifted = addMonths({ year: Number(y), month: Number(mm) }, deltaMonths);
    return formatMonthYearSlash(shifted);
  });

  // 3) YYYY-MM
  out = out.replace(/\b(\d{4})-(0[1-9]|1[0-2])\b/g, (_m, y: string, mm: string) => {
    const shifted = addMonths({ year: Number(y), month: Number(mm) }, deltaMonths);
    return formatMonthYearDash(shifted);
  });

  // 4) Quarter: Qn YYYY
  out = out.replace(/\bQ([1-4])\s+(\d{4})\b/g, (_m, qRaw: string, yRaw: string) => {
    const q = Number(qRaw);
    const y = Number(yRaw);
    const quarterStartMonth = (q - 1) * 3 + 1; // 1,4,7,10
    const shiftedStart = addMonths({ year: y, month: quarterStartMonth }, deltaMonths);
    const newQ = Math.floor((shiftedStart.month - 1) / 3) + 1;
    return `Q${newQ} ${shiftedStart.year}`;
  });

  // 5) Half-year: H1/H2 YYYY
  out = out.replace(/\bH([1-2])\s+(\d{4})\b/g, (_m, hRaw: string, yRaw: string) => {
    const h = Number(hRaw);
    const y = Number(yRaw);
    const halfStartMonth = h === 1 ? 1 : 7;
    const shiftedStart = addMonths({ year: y, month: halfStartMonth }, deltaMonths);
    const newH = shiftedStart.month <= 6 ? 1 : 2;
    return `H${newH} ${shiftedStart.year}`;
  });

  // 6) "full year YYYY"
  out = out.replace(/\b(full year)\s+(\d{4})\b/gi, (m, prefix: string, yRaw: string) => {
    // Anchor at Dec of that year, then shift, then keep wording.
    const shifted = addMonths({ year: Number(yRaw), month: 12 }, deltaMonths);
    return `${prefix} ${shifted.year}`;
  });

  // 7) "YYYY year-end"
  out = out.replace(/\b(\d{4})\s+(year-end)\b/gi, (m, yRaw: string, suffix: string) => {
    const shifted = addMonths({ year: Number(yRaw), month: 12 }, deltaMonths);
    return `${shifted.year} ${suffix}`;
  });

  return out;
}

/**
 * Anchors reporting periods in `text` to the provided `latest` month/year.
 *
 * Intended for example questions that should always "feel current":
 * - Any single month/year becomes `latest` (preserving month-name style where possible).
 * - Month ranges keep the same span but end at `latest`.
 * - Quarters / half-years update to the period containing `latest`.
 * - Quarter lists like "Q1, Q2 and Q3 2025" are shifted so the last quarter matches `latest`'s quarter.
 */
export function anchorReportingPeriodsToLatest(text: string, latest: MonthYear): string {
  let out = text;

  // A) Quarter list: "Q1, Q2 and Q3 2025"
  out = out.replace(
    /\bQ([1-4])\s*,\s*Q([1-4])\s*(?:and|&)\s*Q([1-4])\s+(\d{4})\b/g,
    (_m, q1Raw: string, q2Raw: string, q3Raw: string, yearRaw: string) => {
      const baseYear = Number(yearRaw);
      const qs = [Number(q1Raw), Number(q2Raw), Number(q3Raw)];
      const lastQ = qs[2];
      const latestQ = quarterOf(latest);
      const deltaQ = toQuarterIndex(latest.year, latestQ) - toQuarterIndex(baseYear, lastQ);

      const shifted = qs.map(q => fromQuarterIndex(toQuarterIndex(baseYear, q) + deltaQ));
      const years = new Set(shifted.map(s => s.year));

      if (years.size === 1) {
        const y = shifted[0].year;
        return `Q${shifted[0].quarter}, Q${shifted[1].quarter} and Q${shifted[2].quarter} ${y}`;
      }

      // Cross-year: be explicit per-quarter
      return `Q${shifted[0].quarter} ${shifted[0].year}, Q${shifted[1].quarter} ${shifted[1].year} and Q${shifted[2].quarter} ${shifted[2].year}`;
    }
  );

  // B) Month range without first year: "Aug to Oct 2025" (also covers "Jan to Oct 2025")
  out = out.replace(/\b([A-Za-z]{3,9})\s+to\s+([A-Za-z]{3,9})\s+(\d{4})\b/g, (match, startMonRaw: string, endMonRaw: string, endYearRaw: string) => {
    const startMonth = toMonthNum(startMonRaw);
    const endMonth = toMonthNum(endMonRaw);
    if (!startMonth || !endMonth) return match;

    const end = { year: Number(endYearRaw), month: endMonth };
    const start = { year: Number(endYearRaw), month: startMonth };
    const span = diffMonths(start, end);

    const newEnd = latest;
    const newStart = addMonths(latest, -span);

    const startStyle: 'short' | 'long' = startMonRaw.length <= 3 ? 'short' : 'long';
    const endStyle: 'short' | 'long' = endMonRaw.length <= 3 ? 'short' : 'long';

    const startLabel = `${formatMonthName(newStart.month, startStyle)}${newStart.year !== newEnd.year ? ` ${newStart.year}` : ''}`;
    const endLabel = `${formatMonthName(newEnd.month, endStyle)} ${newEnd.year}`;
    return `${startLabel} to ${endLabel}`;
  });

  // C) Month range with both years: "Jan 2025 to July 2025" / "Jan 2025 - Jun 2025"
  out = out.replace(
    /\b([A-Za-z]{3,9})\s+(\d{4})\s+(?:to|-)\s+([A-Za-z]{3,9})\s+(\d{4})\b/g,
    (match, startMonRaw: string, startYearRaw: string, endMonRaw: string, endYearRaw: string) => {
      const startMonth = toMonthNum(startMonRaw);
      const endMonth = toMonthNum(endMonRaw);
      if (!startMonth || !endMonth) return match;

      const start = { year: Number(startYearRaw), month: startMonth };
      const end = { year: Number(endYearRaw), month: endMonth };
      const span = diffMonths(start, end);

      const newEnd = latest;
      const newStart = addMonths(latest, -span);

      const startStyle: 'short' | 'long' = startMonRaw.length <= 3 ? 'short' : 'long';
      const endStyle: 'short' | 'long' = endMonRaw.length <= 3 ? 'short' : 'long';

      const startLabel = `${formatMonthName(newStart.month, startStyle)} ${newStart.year}`;
      const endLabel = `${formatMonthName(newEnd.month, endStyle)} ${newEnd.year}`;
      const sep = match.includes(' - ') ? ' - ' : ' to ';
      return `${startLabel}${sep}${endLabel}`;
    }
  );

  // D) Single month name + year => latest (preserve month-name style), but avoid touching months
  // that are part of a range we already handled above (e.g. "Jan 2025 to July 2025").
  out = out.replace(
    /(?<!\bfrom\s)(?<!\bto\s)(?<!-\s)\b([A-Za-z]{3,9})\s+(\d{4})\b(?!\s+(?:to|-)\s+[A-Za-z]{3,9}\s+\d{4})/g,
    (match, monRaw: string, _yearRaw: string) => {
      const monthNum = toMonthNum(monRaw);
      if (!monthNum) return match;
      const style: 'short' | 'long' = monRaw.length <= 3 ? 'short' : 'long';
      return `${formatMonthName(latest.month, style)} ${latest.year}`;
    }
  );

  // E) Numeric month range "from YYYY-MM to YYYY-MM" – preserve span, anchor end at latest
  out = out.replace(
    /\bfrom\s+(\d{4})-(0[1-9]|1[0-2])\s+to\s+(\d{4})-(0[1-9]|1[0-2])\b/gi,
    (_m, y1: string, m1: string, y2: string, m2: string) => {
      const start = { year: Number(y1), month: Number(m1) };
      const end = { year: Number(y2), month: Number(m2) };
      const span = diffMonths(start, end);

      const newEnd = latest;
      const newStart = addMonths(latest, -span);

      return `from ${formatMonthYearDash(newStart)} to ${formatMonthYearDash(newEnd)}`;
    }
  );

  // F) YYYY/MM and YYYY-MM => latest (keep same delimiter)
  out = out.replace(/\b(\d{4})\/(0[1-9]|1[0-2])\b/g, () => formatMonthYearSlash(latest));
  out = out.replace(/\b(\d{4})-(0[1-9]|1[0-2])\b/g, () => formatMonthYearDash(latest));

  // G) Quarter: Qn YYYY => quarter containing latest
  out = out.replace(/\bQ([1-4])\s+(\d{4})\b/g, () => `Q${quarterOf(latest)} ${latest.year}`);

  // H) Half-year: H1/H2 YYYY => half containing latest
  out = out.replace(/\bH([1-2])\s+(\d{4})\b/g, () => `H${halfOf(latest)} ${latest.year}`);

  // I) "full year YYYY" => latest year
  out = out.replace(/\b(full year)\s+(\d{4})\b/gi, (_m, prefix: string) => `${prefix} ${latest.year}`);

  // J) "YYYY year-end" => latest year year-end
  out = out.replace(/\b(\d{4})\s+(year-end)\b/gi, (_m, _y: string, suffix: string) => `${latest.year} ${suffix}`);

  return out;
}

